import * as React from 'react';

import { Box, Button, ButtonGroup, InputAdornment, OutlinedInput, Stack } from '@mui/material';

import { CheckboxMultiSelect, CheckboxOption } from '../../../components/CheckboxMultiSelect/CheckboxMultiSelect';
import Icons from '../../../themes/icons';

export type ContentPageView = 'grid' | 'list';

export type ContentTypeFilter = 'all' | 'story' | 'feed' | 'reel' | 'tiktok';

export type ContentCounts = {
  all: number;
  story: number;
  feed: number;
  reel: number;
  tiktok: number;
};

type TypeFilterButtonProps = {
  label: string;
  icon?: React.ReactNode;
  count: number;
  active: boolean;
  onClick: () => void;
  isFirst: boolean;
  isLast: boolean;
};

const TypeFilterButton: React.FC<TypeFilterButtonProps> = ({
  label,
  icon,
  count,
  active,
  onClick,
  isFirst,
  isLast,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '7px',
      height: '100%',
      minHeight: 40,
      border: 'none',
      borderRight: isLast ? 'none' : '1px solid #EFEEEE',
      borderRadius: 0,
      borderTopLeftRadius: isFirst ? '8px' : 0,
      borderBottomLeftRadius: isFirst ? '8px' : 0,
      borderTopRightRadius: isLast ? '8px' : 0,
      borderBottomRightRadius: isLast ? '8px' : 0,
      paddingLeft: '17px',
      paddingRight: '17px',
      backgroundColor: active ? '#F3F3F3' : 'transparent',
      color: active ? '#000' : '#757575',
      fontWeight: active ? 600 : 500,
      cursor: 'pointer',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontSize: '0.875rem',
      '&:hover': {
        backgroundColor: active ? '#F3F3F3' : 'rgba(0, 0, 0, 0.04)',
      },
    }}
  >
    {icon ? (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          color: active ? 'inherit' : 'rgba(17, 17, 17, 0.38)',
          '& > svg': { display: 'block' },
        }}
      >
        {icon}
      </Box>
    ) : null}
    {label ? <span>{label}</span> : null}
    <span style={{ opacity: 0.8 }}>({count})</span>
  </Box>
);

type ContentHeaderProps = {
  counts?: ContentCounts;
  contentType: ContentTypeFilter;
  onContentTypeChange: (value: ContentTypeFilter) => void;
  offerOptions: CheckboxOption[];
  selectedOffers: CheckboxOption[];
  onOffersChange: (value: CheckboxOption | CheckboxOption[] | null) => void;
  searchText: string;
  onSearchChange: (value: string) => void;
  view: ContentPageView;
  onViewChange: (value: ContentPageView) => void;
  showPlatformFilters?: boolean;
};

export const ContentHeader: React.FC<ContentHeaderProps> = ({
  counts = { all: 0, story: 0, feed: 0, reel: 0, tiktok: 0 },
  contentType,
  onContentTypeChange,
  offerOptions,
  selectedOffers,
  onOffersChange,
  searchText,
  onSearchChange,
  view,
  onViewChange,
  showPlatformFilters = true,
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={1.5}
      sx={{ flexWrap: 'nowrap', minWidth: 0 }}
    >
      {showPlatformFilters && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            height: 40,
            border: '1px solid #EFEEEE',
            borderRadius: '8px',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <TypeFilterButton
            label="All"
            count={counts.all}
            active={contentType === 'all'}
            onClick={() => onContentTypeChange('all')}
            isFirst
            isLast={false}
          />
          <TypeFilterButton
            label=""
            icon={<Icons.Content1StoryIcon width={26} height={26} />}
            count={counts.story}
            active={contentType === 'story'}
            onClick={() => onContentTypeChange('story')}
            isFirst={false}
            isLast={false}
          />
          <TypeFilterButton
            label=""
            icon={<Icons.ContentFeedIcon width={26} height={26} />}
            count={counts.feed}
            active={contentType === 'feed'}
            onClick={() => onContentTypeChange('feed')}
            isFirst={false}
            isLast={false}
          />
          <TypeFilterButton
            label=""
            icon={<Icons.ContentReelIcon width={26} height={26} />}
            count={counts.reel}
            active={contentType === 'reel'}
            onClick={() => onContentTypeChange('reel')}
            isFirst={false}
            isLast={false}
          />
          <TypeFilterButton
            label=""
            icon={
              <Icons.TiktokIcon
                width={15}
                height={17}
                style={{ transform: 'translateY(-1px)' }}
              />
            }
            count={counts.tiktok}
            active={contentType === 'tiktok'}
            onClick={() => onContentTypeChange('tiktok')}
            isFirst={false}
            isLast
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{ minWidth: '200px' }}>
          <CheckboxMultiSelect
            options={offerOptions}
            value={selectedOffers}
            onChange={onOffersChange}
            placeholder="All offers"
            multiSelectDisplayText={(count) => `${count} offers selected`}
          />
        </Box>

        <OutlinedInput
          placeholder="Search..."
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <Icons.SearchIcon />
            </InputAdornment>
          }
          size="small"
          sx={{
            minWidth: '290px',
            borderRadius: '8px',
            px: 1.5,
            py: 0.2,
            fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.75,
            color: '#000',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #EFEEEE',
              borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #EFEEEE',
              borderWidth: '1px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #EFEEEE',
              borderWidth: '1px',
            },
          }}
        />

        <ButtonGroup variant="outlined" size="medium">
          <Button
            sx={{
              border: '1px solid #EFEEEE',
              ...(view === 'grid' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
            }}
            onClick={() => onViewChange('grid')}
          >
            <Icons.GridIconV2 />
          </Button>
          <Button
            sx={{
              border: '1px solid #EFEEEE',
              ...(view === 'list' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
            }}
            onClick={() => onViewChange('list')}
          >
            <Icons.ListIconV2 />
          </Button>
        </ButtonGroup>
      </Box>
    </Stack>
  );
};
